import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ProductsData } from "../../../../../network/api/ProductsData";
import { useAppContext } from "../../../../../../../context/AppContext";
import { encodeId } from "../../../../../../../utils/hash";
import Pagination from "../../../../../../../components/Pagination";
import Toast from "../../../../../../../components/Toast";
import CardLoader from "../../../../../../../components/CardLoader";

export default function ProductList() {
    const { appData } = useAppContext();
    const [products, setProducts] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);
    const [toastConfig, setToastConfig] = useState({
        message: "",
        type: "primary",
        visible: false
    });
    const [pageProps, setPageProps] = useState({
        page: 1,
        totalPages: 1,
        totalEntries: 0,
        entryLimit: 20,
        visiblePages: []
    });
    const [pageFilters, setPageFilters] = useState({
        app_id: appData.id || null,
        status: "active",
        visibility: "public",
        page: 1,
        name: null,
        slug: null,
        limit: 20
    });

    useEffect(() => {
        const fetchProducts = async () => {
            setIsLoading(true);
            setErrorMessage(null);

            try {
                const response = await ProductsData({ app_id: appData.id, pageFilters: pageFilters });
                if (response.status === "success") {
                    const sortedProducts = [...response.data.data].sort((a, b) => {
                        const aMeta = typeof a.metadata === "string" ? JSON.parse(a.metadata || "{}") : a.metadata || {};
                        const bMeta = typeof b.metadata === "string" ? JSON.parse(b.metadata || "{}") : b.metadata || {};
                        return (bMeta.featured === true ? 1 : 0) - (aMeta.featured === true ? 1 : 0);
                    });
                    setProducts(sortedProducts);
                    //console.log(response.data.data);
                    setPageProps({
                        page: response.data.page,
                        totalPages: response.data.total_pages,
                        totalEntries: response.data.total_records,
                        entryLimit: response.data.limit,
                        visiblePages: response.data.visible_pages,
                    });
                } else {
                    setErrorMessage({ code: response.code, message: response.message });
                }
            } catch (err) {
                setErrorMessage({ code: "500", message: "Failed to load products." });
            } finally {
                setIsLoading(false);
            }
        };
        fetchProducts();
    }, [pageFilters, appData]);

    const handlePageChange = (page) => {
        if (page >= 1 && page <= pageProps.totalPages) {
            setPageProps((prev) => ({ ...prev, page }));
            setPageFilters((prev) => ({ ...prev, page }));
        }
    };


    return (
        < >
            <Toast
                message={toastConfig.message}
                type={toastConfig.type}
                visible={toastConfig.visible}
                setVisible={(visible) => setToastConfig({ ...toastConfig, visible })}
            />
            {errorMessage && <CardLoader errorMessage={errorMessage} onRetry={() => setPageFilters({ ...pageFilters })} />}
            {isLoading && (
                <div className="text-center my-5 w-100">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-3 text-muted">Loading products...</p>
                </div>
            )}
            {!isLoading && products.length === 0 && (
                <div className="text-center w-100 my-5">
                    <div style={{ fontSize: "3rem", color: "#ccc" }}>
                        <i className="fa fa-folder-open"></i>
                    </div>
                    <h5 className="mt-3 text-muted">No products found</h5>
                </div>
            )}
            {products.map((product) => (
                <div className="col-lg-12 col-xxl-4 col-xl-6" key={product.id}>
                    <div className={`card ${product?.featured ? "card-primary" : ""}`}>
                        {(() => {
                            let metadata = {};
                            try {
                                metadata = typeof product.metadata === "string" ? JSON.parse(product.metadata) : product.metadata;
                            } catch (_) { }

                            if (metadata && metadata.featured) {
                                return <span className="badge bg-primary position-absolute top-0 start-0 m-2">Featured</span>;
                            }
                            return null;
                        })()}
                        <div className="card-body">
                            <div className="row m-b-30">
                                <div className="col-md-5 col-xxl-12">
                                    <div className="new-arrival-product mb-4 mb-xxl-4 mb-md-0">
                                        <div className="new-arrivals-img-contnent">
                                            {product?.image && (
                                                <img
                                                    src={product?.image}
                                                    alt={product.name}
                                                    className="img-fluid rounded"
                                                    onError={(e) => {
                                                        e.target.onerror = null;
                                                        e.target.src = "https://www.shutterstock.com/image-vector/default-ui-image-placeholder-wireframes-600nw-1037719192.jpg";
                                                    }}
                                                />)}
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-7 col-xxl-12">
                                    <div className="new-arrival-content position-relative">
                                        <h4><Link to={`/shop/${product.slug}`}>{product.name}</Link></h4>
                                        <div className="comment-review star-rating">
                                            {product?.metadata != null && (() => {
                                                let metadata = {};
                                                try {
                                                    metadata = typeof product.metadata === "string" ? JSON.parse(product.metadata) : product.metadata;
                                                } catch (_) { }

                                                return (
                                                    <>
                                                        {metadata.cancelled_price && (
                                                            <p className="price mb-0">
                                                                <del className="text-muted me-2">${metadata.cancelled_price}</del>
                                                                <span className="text-danger fw-bold">${product.price}</span>
                                                            </p>
                                                        )}
                                                        {!metadata.cancelled_price && <p className="price">${product.price}</p>}
                                                    </>
                                                );
                                            })()}
                                        </div>
                                        <p>Availability: <span className="item"> In stock <i className="fa fa-check-circle text-success"></i></span></p>
                                        <p>Product code: <span className="item">{encodeId(product.id)}</span></p>
                                        <p className="text-content">{product.description}</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            ))}
            <div className="dataTables_wrapper">
                <Pagination pageProps={pageProps} onPageChange={handlePageChange} />
            </div>
        </>
    );
}